/** Shared display helpers for archival sales-order UI. */

export const fmt = (n) => (typeof n === 'number' ? n.toLocaleString() : (n ?? '—'));

export const fmtAmt = (n) =>
  n == null || n === ''
    ? '—'
    : Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** ISO date part only — avoids TZ day-shift from `new Date('YYYY-MM-DD')`. */
export const fmtDate = (t) => {
  if (t == null || t === '') return '—';
  const s = String(t);
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : s.slice(0, 10);
};

/** Prefer rollup ordered_amt; fall back to Epicor header DocTotalCharges. */
export const orderedDisplay = (row) => {
  const rollup = row?.ordered_amt;
  if (rollup != null && rollup !== '' && Number(rollup) !== 0) return rollup;
  return row?.doc_total_charges ?? rollup;
};

/**
 * Net extended price for an order or invoice line.
 *
 * Epicor stores ext price as `qty * list price`, i.e. GROSS of the discount, and
 * reports the discount as a separate amount. Subtracting is therefore required —
 * reading `doc_ext_price` straight out shows the pre-discount value.
 */
export const netExt = (row) => {
  const ext = Number(row?.doc_ext_price_dtl ?? row?.doc_ext_price);
  if (!Number.isFinite(ext)) return null;
  const disc = Number(row?.doc_discount ?? 0);
  return ext - (Number.isFinite(disc) ? disc : 0);
};

/** Net-of-discount price per unit; falls back to unit price when qty is 0/absent. */
export const netUnitPrice = (row) => {
  const qty = Number(row?.selling_quantity ?? row?.selling_ship_qty);
  const net = netExt(row);
  if (net == null || !Number.isFinite(qty) || qty === 0) {
    return row?.doc_unit_price ?? null;
  }
  return net / qty;
};

/** Discount as a percentage when Epicor set one, else the raw amount. */
export const discountDisplay = (row) => {
  const pct = Number(row?.discount_percent);
  if (Number.isFinite(pct) && pct !== 0) return `${pct}%`;
  const amt = Number(row?.doc_discount);
  if (Number.isFinite(amt) && amt !== 0) return fmtAmt(amt);
  return '—';
};

/** Misc charge amount; percentage-type rows carry no amount, only a rate. */
export const miscAmountDisplay = (row) => {
  if (row?.misc_type === 'P') {
    const pct = Number(row?.percentage);
    return Number.isFinite(pct) ? `${pct}%` : '—';
  }
  return fmtAmt(row?.doc_misc_amt);
};

export const datesSuspect = (order) => {
  if (!order) return false;
  const raw = order.order_date;
  const eff = order.order_date_effective;
  if (raw == null || eff == null) return false;
  return String(raw).slice(0, 10) !== String(eff).slice(0, 10);
};
